import type { ChangeEvent } from 'react';
import { PackageNameFieldBase } from '../../shared/PackageNameFieldBase';

interface PackageNameFieldProps {
  value: string;
  error: string | null;
  isChecking: boolean;
  showSpinner: boolean;
  isValid: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

function PackageNameSpinner({ visible }: { visible: boolean }) {
  return (
    <div
      className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-opacity duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <svg
        className="animate-spin text-neutral-400"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-label="Checking package name"
      >
        <title>Checking package name</title>
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>
  );
}

function PackageNameValidIndicator({ visible }: { visible: boolean }) {
  return (
    <div
      className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500 pointer-events-none transition-opacity duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={3}
        stroke="currentColor"
        aria-label="Package name available"
      >
        <title>Package name available</title>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 12.75l6 6 9-13.5"
        />
      </svg>
    </div>
  );
}

export function PackageNameField({
  value,
  error,
  isChecking,
  showSpinner,
  isValid,
  onChange,
}: PackageNameFieldProps) {
  return (
    <PackageNameFieldBase
      value={value}
      required
      description="Enter the exact Android application ID from your APK (case-sensitive), e.g., com.example.app."
      error={error}
      showError={!isChecking}
      onChange={onChange}
      inputClassName="border-neutral-700 border h-10 outline-none px-4 pr-10 rounded-xl text-sm text-white transition-colors w-full focus:border-neutral-300 hover:border-neutral-500 placeholder:text-neutral-400"
      afterInput={
        <>
          <PackageNameSpinner visible={showSpinner} />
          <PackageNameValidIndicator
            visible={!showSpinner && isValid && Boolean(value)}
          />
        </>
      }
    />
  );
}
